import React, { FC, useEffect, useRef } from 'react';
import { View } from 'react-native';
import { OutPortal } from 'react-reverse-portal';
import { NavigationProp, useIsFocused } from '@react-navigation/native';
import { TWCBackHandler, TWCPage } from '@components';
import { useRecoilState, useRecoilValue } from 'recoil';
import { PortalDestination } from 'rn-native-portals';

import {
  alertsModalPageTestIDs,
  height,
  LiveControlsTestIDs,
  livePageTestIDs,
  menuTestIDs,
  Theme,
  width,
} from '@constants';
import {
  useAppContextNodeHandle,
  useAppContextReference,
  useGetGeoLocation,
  useIsVideoFocused,
  useLiveRamp,
  useVideoNodePortal,
} from '@hooks';
import { anfState, liveVideoPlayerState, userSettingsState } from '@store';

import { onBackPress } from './helpers';
import { styles } from './styles';

import { RadialGradientBackground } from '../../components/loading/RadialGradientBackground';
import { LDLBase } from '../../components/lowerDisplayLine/LDLBase/LDLBase';
import { segmentEventTypes } from '../../components/segment/segmentEventTypes';
import { segmentTrackEvent } from '../../components/segment/SegmentTrackEvent';
import { isAndroid, isWeb } from '../../helpers/Platform';
import { FOCUS_NOW } from '../../helpers/Tools';
import { VideoContext } from '../../hooks/useIsVideoFocused';
import { liveVideoStreamTypes } from '../../hooks/useLiveStreamSelector';

type LivePageProps = {
  navigation: NavigationProp<Record<string, unknown>>;
};

export const LivePage: FC<LivePageProps> = ({ navigation }) => {
  const isVideoFocused = useIsVideoFocused(VideoContext.live);
  const [{ data, isLoading, error, adTrackingData, currentLiveVideoStream }, setLiveVideoState] =
    useRecoilState(liveVideoPlayerState);
  const anfData = useRecoilValue(anfState);
  const liveVideoPlayerNode = useVideoNodePortal();
  const menuReference = useAppContextReference(menuTestIDs.mainMenu);
  const menuHandle = useAppContextNodeHandle(menuTestIDs.mainMenu);
  const geoLocation = useGetGeoLocation();
  const [userSettings, setUserSettings] = useRecoilState(userSettingsState);
  const segmentLocationWatchingFired = useRef(false);

  const isSpanishSelected = currentLiveVideoStream === liveVideoStreamTypes.videoStreamSpanish;
  const anfSelected = anfData?.data?.find(anf => anf.selected);
  const hideLDL = isSpanishSelected && !anfSelected;

  const isPageFocused = useIsFocused();

  const pageSetFirstFocus = useRef(false);
  const locationModalOpen = useRef(false);

  const liveFirstFocus = useAppContextReference(LiveControlsTestIDs.liveVideoControlsExit);
  const pendingLiveFocus = useRef(false);

  const { appAccessed } = userSettings;

  // Use flag to set focus. Simple check on each render- useEffect will not work with refs
  if (pendingLiveFocus.current && liveFirstFocus.current) {
    pendingLiveFocus.current = false;
    FOCUS_NOW(liveFirstFocus);
  }

  useEffect(() => {
    TWCBackHandler.addEventListener('hardwareBackPress', onBackPress, 5);
    return () => {
      TWCBackHandler.removeEventListener('hardwareBackPress', onBackPress);
    };
  }, []);

  useEffect(() => {
    if (appAccessed) {
      if (!segmentLocationWatchingFired.current) {
        // Send the location they confirmed in the modal
        segmentTrackEvent(segmentEventTypes.LOCATION_WATCHING, userSettings);
        segmentLocationWatchingFired.current = true;
      }
    } else if (geoLocation.city && isPageFocused) {
      locationModalOpen.current = true;
      setUserSettings(currentState => ({
        ...currentState,
        appAccessed: true,
      }));
      navigation.navigate(alertsModalPageTestIDs.alertsModalPage, {
        type: 'alertLocation',
      });
      pageSetFirstFocus.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoLocation.city, appAccessed]);

  useEffect(() => {
    if (isPageFocused) {
      setLiveVideoState(currentState => ({
        ...currentState,
        width,
        height,
        stopped: false,
        ...(isAndroid && {
          isFullScreen: true,
          isScreenFocused: true,
          nextFocusUp: menuHandle,
        }),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuHandle, isPageFocused]);

  const onPageFocus = () => {
    if (locationModalOpen.current) {
      locationModalOpen.current = false;
      if (isWeb && !pageSetFirstFocus.current) {
        FOCUS_NOW(menuReference);
        pageSetFirstFocus.current = true;
      }
      return false;
    }

    if (pageSetFirstFocus.current) {
      if (liveFirstFocus.current == null) {
        pendingLiveFocus.current = true;
      } else {
        FOCUS_NOW(liveFirstFocus);
      }
    } else {
      FOCUS_NOW(menuReference);
      pageSetFirstFocus.current = true;
    }
    // We do not want default behavior, so return false
    return false;
  };

  useLiveRamp();

  return (
    <TWCPage childIds={[]} hasBackground={false} isMenuItem pageID={livePageTestIDs.livePage} onPageFocus={onPageFocus}>
      <View collapsable={false} style={styles.fullscreen}>
        <RadialGradientBackground>
          <View collapsable={false} style={styles.radialGradientBackgroundContainer}>
            <View
              collapsable={false}
              style={{
                height: Theme.appHeight - Theme.px(102),
              }}
            >
              {isVideoFocused ? (
                <>
                  {isWeb ? (
                    <OutPortal
                      adData={adTrackingData}
                      data={data}
                      error={error}
                      height={Theme.appHeight}
                      isFullscreen
                      isLoading={isLoading}
                      isScreenFocused
                      nextFocusUp={menuHandle}
                      node={liveVideoPlayerNode}
                      width={Theme.appWidth}
                    />
                  ) : (
                    <PortalDestination
                      collapsable={false}
                      hight={Theme.px(height)}
                      name={'liveVideoPlayer'}
                      width={Theme.px(width)}
                    />
                  )}
                </>
              ) : null}
            </View>
            {!hideLDL && (
              <View style={styles.ldlWrapper}>
                <LDLBase.API />
              </View>
            )}
          </View>
        </RadialGradientBackground>
      </View>
    </TWCPage>
  );
};
